import React from 'react';
import { motion } from 'framer-motion'; 
import ProjectStatsBadge from '../../../components/ProjectStatsBadge';
import ShareProjectButton from '../../../components/ShareProjectButton/ShareProjectButton';
import './linkedin-clone.css';
import linkedinCloneImage from '../../../assets/images/linkedinuiclone.png'; 

const LinkedInUIClone = () => {
  return (
    <motion.div 
      className="project-detail-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.h1 
        className="project-heading"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        LinkedIn <span>UI Clone</span>
      </motion.h1>

      <motion.p 
        className="last-updated"
        initial={{ x: 50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        <br />
        Last Updated On: 11th July 2026
      </motion.p>

      <div className="project-content">
        <motion.div 
          className="project-left"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <img src={linkedinCloneImage} alt="LinkedIn UI Clone" className="project-image" />
          <h2 className="project-subtitle">LinkedIn Clone</h2>
          <span className="project-category">Website</span>
          <p className="project-author">Author: Praveen Kumar B</p>

          <ProjectStatsBadge docId="linkedinClone" trackView={true} />

          {/* Buttons */}
          <div className="project-buttons">
            <a href="https://project-tap-assignment-8.netlify.app/" className="visit-project-button">
              → Visit the Project
            </a>
            <a href="https://github.com/Praveenkumar-king/LinkedIn-Clone" className="visit-project-button">
              → View on GitHub
            </a>
            <ShareProjectButton
              project={{
                id: "linkedinClone",
                title: "LinkedIn UI Clone",
                path: "/projects/projectdetail/linkedin-clone"
              }}
            />
          </div>
        </motion.div>

        <motion.div 
          className="project-right"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h3 className="section-title">Project Details</h3>
          <p className="overview-text">
            Welcome to the <strong>LinkedIn UI Clone</strong>! Here's a detailed overview of the professional networking platform interface.
          </p>

          <h3 className="section-title">Overview 🌟</h3>
          <p className="overview-description">
            The <strong>LinkedIn UI Clone</strong> is a modern and responsive professional networking platform inspired by LinkedIn. It recreates the overall user experience with a clean interface, professional news feed, profile pages, job listings, messaging, and networking features while demonstrating modern front-end development practices.
          </p>

          <h3 className="section-title">Key Features 🛠️</h3>
          <ul className="feature-list">
            <li><strong>Professional Home Feed</strong> 💼 - Browse industry updates, articles, and professional posts.</li>
            <li><strong>Profile Dashboard</strong> 👤 - View professional profiles, skills, experience, and education.</li>
            <li><strong>Jobs Section</strong> 💻 - Explore job opportunities with a modern listing interface.</li>
            <li><strong>My Network</strong> 🤝 - Discover professionals and manage networking connections.</li>
            <li><strong>Messaging Interface</strong> 💬 - Clean and responsive professional chat layout.</li>
            <li><strong>Responsive Navigation</strong> 📱 - Optimized for desktop, tablet, and mobile devices.</li>
            <li><strong>Modern Dashboard</strong> 🎨 - Professional UI inspired by LinkedIn's clean design.</li>
          </ul>

          <h3 className="section-title">How the Website Works 🔍</h3>
          <ul className="how-it-works-list">
            <li><strong>Browse Feed</strong> 📰 - Explore professional posts and company updates.</li>
            <li><strong>Search Jobs</strong> 💼 - Discover career opportunities from various companies.</li>
            <li><strong>Build Network</strong> 🤝 - Connect with professionals and expand your network.</li>
            <li><strong>Visit Profiles</strong> 👤 - Explore professional profiles, skills, and achievements.</li>
          </ul>

          <h3 className="section-title">Features & Benefits 🚀</h3>
          <ul className="benefits-list">
            <li><strong>Professional Interface</strong> 🎨 - Clean dashboard designed for business networking.</li>
            <li><strong>Responsive Experience</strong> 📱 - Optimized across mobile, tablet, and desktop devices.</li>
            <li><strong>Interactive Components</strong> 👍 - Professional feed, messaging, and networking features.</li>
            <li><strong>Modern User Experience</strong> ✨ - Smooth navigation with responsive layouts and animations.</li>
          </ul>

          <h3 className="section-title">Technical Details ⚙️</h3>
          <ul className="technical-details-list">
            <li><strong>Frontend</strong> 🖥️ - React.js, HTML5, CSS3, and JavaScript (ES6).</li>
            <li><strong>Animations</strong> ✨ - Framer Motion for smooth page transitions.</li>
            <li><strong>Responsive Design</strong> 📱 - CSS Flexbox, Grid, and Media Queries.</li>
            <li><strong>Deployment</strong> ☁️ - Hosted on Netlify with GitHub for version control.</li>
          </ul>

          <h3 className="section-title">Real-Life Applications 🌍</h3>
          <ul className="real-life-applications-list">
            <li><strong>Professional Networking</strong> 🤝 - Connect professionals and recruiters.</li>
            <li><strong>Job Portals</strong> 💼 - Career search and recruitment platforms.</li>
            <li><strong>Business Communities</strong> 🏢 - Corporate networking and collaboration websites.</li>
            <li><strong>Learning Front-End UI</strong> 💻 - Practice building responsive dashboard interfaces.</li>
          </ul>

          <h3 className="section-title">Future Features 🔮</h3>
          <ul className="future-features-list">
            <li><strong>User Authentication</strong> 🔐 - Secure login and account management.</li>
            <li><strong>Real-Time Messaging</strong> 💬 - Instant communication between professionals.</li>
            <li><strong>Job Applications</strong> 📄 - Apply for jobs directly through the platform.</li>
            <li><strong>Notifications</strong> 🔔 - Receive updates for jobs, messages, and connections.</li>
          </ul>

          <h3 className="section-title">Conclusion 🎯</h3>
          <p>
            The <strong>LinkedIn UI Clone</strong> recreates the experience of a modern professional networking platform with responsive layouts, career-focused dashboards, networking features, messaging, and job listings. This project showcases advanced front-end development skills, responsive UI design, and the implementation of a professional web application.
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default LinkedInUIClone;